import DynamicModal from '@components/DynamicModal/DynamicModal';
import SelectDateAndTimeForm from '@components/SelectDateAndTimeForm/SelectDateAndTimeForm';
import { ApplicationAction } from '@features/applications';
import UserDetailRow from '@features/employerAccount/components/UserDetailRow/UserDetailRow';
import { useUpdateEnterpriseUpdateCompany } from '@features/profile/hooks/useUpdateEnterpriseUpdateCompany';
import { useUpdateProfile } from '@features/profile/hooks/useUpdateProfile';
import { usetGetSingleCompanyData } from '@features/savedMessages/hooks/usetGetSingleCompanyData';
import CloseIcon from '@mui/icons-material/Close';
import DoneIcon from '@mui/icons-material/Done';
import HomeIcon from '@mui/icons-material/Home';
import Videocam from '@mui/icons-material/Videocam';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  LinearProgress,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Box } from '@mui/system';
import { authContext } from '@staffedupusa/shared-components';
import { hireflixCta, hireflixCtaHover } from '@styles/colors';
import BundledEditor from '@utils/BundledEditor';
import { getStrippedText } from '@utils/helper';
import { isValidHttpUrl } from '@utils/validHttpUrl';
import dayjs from 'dayjs';
import {
  Dispatch,
  FC,
  SetStateAction,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Link } from 'react-router-dom';
import { Editor as TinyMCEEditor } from 'tinymce';

import {
  ArchiveIcon,
  AssignIcon,
  DateIcon,
  DeclineIcon,
  InetriewScheduleIcon,
  JobOfferIcon,
  MessageIcon,
  SavedIcon,
  EditIcon,
} from '../../../../vectors/index';
import AssignTagsModal from '../AssignTagsModal/AssignTagsModal';
import EmployerAssignTagsModal from '../EmployerAssignTagsModal/EmployerAssignTagsModal ';
import HireflixPlanPurchaseModal from '../HireflixPlanPurchaseModal/HireflixPlanPurchaseModal';

type SingleMessageModalProps = {
  isOpen: boolean;
  handleClose: () => void;
  action: ApplicationAction;
  handleSubmit: (isMessageRequired: boolean, message: string) => void;
  errorText: string;
  setErrorText: Dispatch<SetStateAction<string>>;
  title: string;
  subtitle?: string;
  isSubtitleShown?: boolean;
  submitWithMessageText: string;
  submitWithoutMessageText?: string;
  successMessage?: string;
  isSuccessMessageShown?: boolean;
  showProgress?: boolean;
  showSendWithoutMessage?: boolean;
  buttonColor?:
    | 'error'
    | 'success'
    | 'primary'
    | 'lightPurple'
    | 'dark'
    | 'secondary';
  progress?: number;
  disabled?: boolean;
  isDateRangePickerShown?: boolean;
  setStartTimestamp?: Dispatch<SetStateAction<number | null>>;
  setEndTimestamp?: Dispatch<SetStateAction<number | null>>;
  setFormattedDate?: Dispatch<SetStateAction<string>>;
  selectedCompanyId?: string | number;
  selectedApplicationsIds?: number[];
  handlePageNo?: () => void;
  setDefaultType?: any;
  defaultType?: any;
  hireflixInviteLoading?: boolean;
  isInterviewExist?: boolean;
};

const SingleMessageModal: FC<SingleMessageModalProps> = ({
  isOpen,
  handleClose,
  action,
  title,
  subtitle,
  submitWithMessageText,
  submitWithoutMessageText,
  handleSubmit,
  successMessage,
  progress,
  isSuccessMessageShown = false,
  isSubtitleShown = true,
  showProgress = false,
  showSendWithoutMessage = false,
  buttonColor = 'primary',
  errorText,
  selectedCompanyId,
  setErrorText,
  disabled = false,
  isDateRangePickerShown = false,
  setStartTimestamp,
  setEndTimestamp,
  setFormattedDate,
  selectedApplicationsIds,
  handlePageNo,
  setDefaultType,
  defaultType = 'virtual',
  hireflixInviteLoading = false,
  isInterviewExist,
}) => {
  const { authData } = useContext(authContext);

  const isHireflixSync = authData?.user?.hire_flix_sync;

  const testingCompanyId = Number(
    import.meta.env.VITE_HIREFLIX_TESTING_COMPANY,
  );
  const isHireflixTestingCompany =
    authData?.user?.company?.id === testingCompanyId;

  const [hireflixPlanModal, setHireflixPlanModal] = useState(false);
  const [message, setMessage] = useState(action?.message ?? '');
  const [shouldSaveNewDefaultMessage, setShouldSaveNewDefaultMessage] =
    useState<boolean>(false);
  const [initialMessage, setInitialMessage] = useState<string>('');

  const { companyData } = usetGetSingleCompanyData({
    enabled: !!(authData?.user?.enterprise && selectedCompanyId),
    selectedCompanyId,
  });

  const [virtualLocation, setVirtualLocation] = useState('');
  const [physicalLocation, setPhysicalLocation] = useState('');

  const [isUpdateAddressOpen, setIsUpdateAddressOpen] = useState(false);
  const [isVirtualInterviewModal, setIsVirtualInterviewModal] = useState(false);

  const editorRef = useRef<TinyMCEEditor | null>(null);

  const { updateProfile, isUpdatingProfile } = useUpdateProfile({
    setErrorText,
    onSuccess: () => {
      // handleClose();
      setIsUpdateAddressOpen(false);
      setIsVirtualInterviewModal(false);
    },
  });

  const { updateCompanyResponses, isUpdatingCompanyResponses } =
    useUpdateEnterpriseUpdateCompany({
      setErrorText,
      onSuccess: () => {
        handleClose();
      },
    });

  const getResponseName = (field: string) => {
    switch (field) {
      case 'request more info':
        return 'default_request_more_info_message';
      case 'invite to interview block':
        return 'default_interview_block_message';
      case 'Invite to Open Interview':
        return 'default_open_interview_message';
      case 'Scheduled Interview':
        return 'default_interview_message';
      case 'save':
        return 'default_saved_message';
      case 'decline':
        return 'default_declined_message';
      case 'hire':
        return 'default_hire_message';
      case 'archive':
        return 'default_archive_message';
      case 'invite to hireflix interview':
        return 'default_hire_flix_interview_message';
      default:
        return 'default_request_more_info_message';
    }
  };

  function handleVirtualLinkSave() {
    if (virtualLocation && isValidHttpUrl(virtualLocation)) {
      updateProfile({
        virtual_interview_link: virtualLocation || '',
      });
    }
  }

  function handleSave() {
    updateProfile({
      physical_interview_address: defaultType === 'inPerson' ? 'true' : 'false',
      physical_interview_location: physicalLocation || '',
      virtual_interview_link: virtualLocation || '',
    });
  }

  const handleSaveDefaultMessage = () => {
    if (action?.actionType && message.length && shouldSaveNewDefaultMessage) {
      const responseName = getResponseName(action?.actionType);
      if (authData?.user?.company) {
        updateProfile({ [responseName!]: message });
      } else if (authData?.user?.enterprise) {
        updateCompanyResponses({
          [responseName!]: message,
          company_id: selectedCompanyId,
        });
      }
      setShouldSaveNewDefaultMessage(false);
    }
  };

  useEffect(() => {
    if (action) {
      if (authData?.user?.company) {
        if (action.actionType === 'save') {
          setMessage(
            authData?.user?.company.default_saved_message ?? action.message,
          );
          setInitialMessage(
            authData?.user?.company.default_saved_message ?? action.message,
          );
        } else if (
          action.actionType === 'invite to interview block' ||
          action.actionType === 'Invite to interview block'
        ) {
          setMessage(
            authData?.user?.company?.default_interview_block_message ??
              action.message,
          );
          setInitialMessage(
            authData?.user?.company?.default_interview_block_message ??
              action.message,
          );
        } else if (action.actionType === 'Invite to Open Interview') {
          setMessage(
            authData?.user?.company.default_open_interview_message ??
              action.message,
          );
          setInitialMessage(
            authData?.user?.company.default_open_interview_message ??
              action.message,
          );
        } else if (action.actionType === 'invite to hireflix interview') {
          setMessage(
            authData?.user?.company?.default_hire_flix_interview_message ??
              action.message,
          );
          setInitialMessage(
            authData?.user?.company?.default_hire_flix_interview_message ??
              action.message,
          );
        } else if (action.actionType === 'Scheduled Interview') {
          const isHireflix = defaultType === 'hireflix';

          const mesage = isHireflix
            ? authData?.user?.company?.default_hire_flix_interview_message
            : (authData?.user?.company?.default_interview_message ??
              action.message);

          setMessage(mesage);
          setInitialMessage(mesage);
          // setMessage(
          //   authData?.user?.company.default_interview_message ?? action.message,
          // );
          // setInitialMessage(
          //   authData?.user?.company.default_interview_message ?? action.message,
          // );
        } else if (action.actionType === 'hire') {
          setMessage(
            authData?.user?.company.default_hire_message ?? action.message,
          );
          setInitialMessage(
            authData?.user?.company.default_hire_message ?? action.message,
          );
        } else if (action.actionType === 'decline') {
          setMessage(
            authData?.user?.company.default_declined_message ?? action.message,
          );
          setInitialMessage(
            authData?.user?.company.default_declined_message ?? action.message,
          );
        } else if (action.actionType === 'archive') {
          setMessage(
            authData?.user?.company.default_archive_message ?? action.message,
          );
          setInitialMessage(
            authData?.user?.company.default_archive_message ?? action.message,
          );
        } else if (action.actionType === 'request more info') {
          setMessage(
            authData?.user?.company.default_request_more_info_message ??
              action.message,
          );
          setInitialMessage(
            authData?.user?.company.default_request_more_info_message ??
              action.message,
          );
        }
      } else if (authData?.user?.enterprise && companyData) {
        if (action.actionType === 'save') {
          setMessage(
            companyData?.object?.default_saved_message ?? action.message,
          );
          setInitialMessage(
            companyData?.object?.default_saved_message ?? action.message,
          );
        } else if (
          action.actionType === 'invite to interview block' ||
          action.actionType === 'Invite to interview block'
        ) {
          setMessage(
            companyData?.object?.default_interview_block_message ??
              action.message,
          );
          setInitialMessage(
            companyData?.object?.default_interview_block_message ??
              action.message,
          );
        } else if (action.actionType === 'Invite to Open Interview') {
          setMessage(
            companyData?.object?.default_open_interview_message ??
              action.message,
          );
          setInitialMessage(
            companyData?.object?.default_open_interview_message ??
              action.message,
          );
        } else if (action.actionType === 'Scheduled Interview') {
          setMessage(
            companyData?.object?.default_interview_message ?? action.message,
          );
          setInitialMessage(
            companyData?.object?.default_interview_message ?? action.message,
          );
        } else if (action.actionType === 'hire') {
          setMessage(
            companyData?.object?.default_hire_message ?? action.message,
          );
          setInitialMessage(
            companyData?.object?.default_hire_message ?? action.message,
          );
        } else if (action.actionType === 'decline') {
          setMessage(
            companyData?.object?.default_declined_message ?? action.message,
          );
          setInitialMessage(
            companyData?.object?.default_declined_message ?? action.message,
          );
        } else if (action.actionType === 'archive') {
          setMessage(
            companyData?.object?.default_archive_message ?? action.message,
          );
          setInitialMessage(
            companyData?.object?.default_archive_message ?? action.message,
          );
        } else if (action.actionType === 'request more info') {
          setMessage(
            companyData?.object?.default_request_more_info_message ??
              action.message,
          );
          setInitialMessage(
            companyData?.object?.default_request_more_info_message ??
              action.message,
          );
        }
      }
    }
  }, [action, companyData, defaultType]);

  const enterprise_physical_address_concate =
    companyData &&
    companyData?.object?.additional_info?.physical_interview_location
      ? companyData?.object?.additional_info?.physical_interview_location
      : companyData
        ? `${companyData?.object?.s_address1 || companyData?.object?.s_address2 || ''} ${companyData?.object?.s_city || ''} ${companyData?.object?.s_state || ''} ${companyData?.object?.s_zip || ''}`.trim()
        : '';

  const physical_address_concate = authData?.user.company?.additional_info
    ?.physical_interview_location
    ? authData?.user.company?.additional_info?.physical_interview_location
    : `${authData?.user?.company?.s_address1 || authData?.user?.company?.s_address2 || ''} ${authData?.user?.company?.s_city || ''} ${authData?.user?.company?.s_state || ''} ${authData?.user?.company?.s_zip || ''}`.trim();

  useEffect(() => {
    if (
      !authData.user?.enterprise &&
      authData?.user?.company?.additional_info
    ) {
      setPhysicalLocation(physical_address_concate || '');

      setVirtualLocation(
        authData?.user?.company?.additional_info?.virtual_interview_link || '',
      );

      setDefaultType?.(
        authData?.user?.company?.additional_info?.physical_interview_address ===
          'true'
          ? 'inPerson'
          : 'virtual',
      );
    } else if (companyData?.object?.additional_info) {
      setPhysicalLocation(enterprise_physical_address_concate || '');

      setVirtualLocation(
        companyData?.object?.additional_info.virtual_interview_link || '',
      );

      setDefaultType?.(
        companyData?.object?.additional_info?.physical_interview_address ===
          true ||
          companyData?.object?.additional_info?.physical_interview_address ===
            'true'
          ? 'inPerson'
          : 'virtual',
      );
    }
  }, [
    authData?.user?.company?.additional_info,
    companyData?.object?.additional_info,
  ]);

  const isLink = !!(
    !authData.user?.enterprise &&
    defaultType === 'virtual' &&
    !virtualLocation
  );

  const enterprise_virtual_Link = !!(
    authData.user?.enterprise &&
    defaultType === 'virtual' &&
    authData.user?.company?.additional_info?.physical_interview_address ===
      'false' &&
    authData.user?.company?.additional_info?.virtual_interview_link
  );

  const DEFAULT_TRIAL_DAYS = 30;
  const hireflixPlan = authData?.user?.company?.hire_flix_plan;

  const planStatus = hireflixPlan?.status || 'Trial';
  const isTrial = !hireflixPlan || hireflixPlan?.status === 'Trial';
  const trialDaysLeft = hireflixPlan?.trial_start_date
    ? Math.max(
        0,
        DEFAULT_TRIAL_DAYS -
          dayjs().diff(dayjs(hireflixPlan.trial_start_date), 'day'),
      )
    : DEFAULT_TRIAL_DAYS;

  const handleHireflixPlanModal = () => {
    setHireflixPlanModal(!hireflixPlanModal);
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth={action?.actionType !== 'assign Tags' ? 'sm' : 'xs'}
      fullWidth
      disableEnforceFocus
    >
      <DialogTitle>
        <IconButton
          sx={{ position: 'absolute', top: 5, right: 5 }}
          onClick={handleClose}
        >
          <CloseIcon />
        </IconButton>
        <br />
        {action?.actionType === 'decline' ? (
          <DeclineIcon />
        ) : action?.actionType === 'archive' ? (
          <ArchiveIcon />
        ) : action?.actionType === 'hire' ? (
          <JobOfferIcon />
        ) : action?.actionType === 'save' ? (
          <SavedIcon />
        ) : action?.actionType === 'message' ? (
          <MessageIcon />
        ) : action?.actionType === 'assign Tags' ? (
          <AssignIcon />
        ) : action?.actionType === 'invite to interview block' ? (
          <InetriewScheduleIcon />
        ) : action?.actionType === 'invite to hireflix interview' ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '64px',
              height: '64px',
              margin: '0 auto',
              borderRadius: '100%',
              backgroundColor: 'rgb(243, 232, 255)',
              mb: -5,
            }}
          >
            <Videocam sx={{ fontSize: 40, color: hireflixCta }} />
          </Box>
        ) : action?.actionType === 'Scheduled Interview' ? (
          <InetriewScheduleIcon />
        ) : (
          <DateIcon />
        )}
        <br />
        <Typography variant="h4" sx={{ p: 3 }}>
          {title}
          {isSubtitleShown && subtitle && <Typography>{subtitle}</Typography>}
        </Typography>
      </DialogTitle>

      <DialogContent>
        {/* Scheduled Interview' */}
        {action?.actionType === 'Scheduled Interview' &&
          !authData.user?.enterprise && (
            <FormControl sx={{ width: '100%', mt: '10px' }}>
              <RadioGroup
                aria-labelledby="demo-controlled-radio-buttons-group"
                name="controlled-radio-buttons-group"
                value={defaultType} // <-- keep it as "inPerson" | "virtual"
                onChange={(e) => {
                  setDefaultType?.(
                    e.target.value as 'inPerson' | 'virtual' | 'hireflix',
                  );
                  setErrorText('');
                }}
                sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}
              >
                <FormControlLabel
                  value="inPerson"
                  control={<Radio />}
                  label="In Person"
                />
                <FormControlLabel
                  value="virtual"
                  control={<Radio />}
                  label="Virtual"
                />
                {isHireflixTestingCompany &&
                  isHireflixSync &&
                  !isInterviewExist && (
                    <FormControlLabel
                      value="hireflix"
                      control={<Radio />}
                      label="Hireflix"
                    />
                  )}
              </RadioGroup>

              {defaultType === 'inPerson' && (
                <Box
                  sx={{
                    border: '1px solid #e4ecf2',
                    px: '10px',
                    borderRadius: '10px',
                  }}
                >
                  <Typography
                    fontWeight="bold"
                    variant="body2"
                    mt="3px"
                    paddingTop="2px"
                  >
                    {authData?.user?.company?.s_name}
                  </Typography>

                  <Box sx={{ mt: '-10px', textAlign: 'left' }}>
                    <UserDetailRow
                      label=""
                      value={physical_address_concate || ' '}
                      action={() => setIsUpdateAddressOpen(true)}
                      isEdit
                      noLabel
                      textAlign
                    />
                  </Box>
                </Box>
              )}

              {defaultType === 'virtual' && (
                <>
                  <Box
                    sx={{
                      border: '1px solid #e4ecf2',
                      px: '10px',
                      borderRadius: '10px',
                    }}
                  >
                    <UserDetailRow
                      label=""
                      noLabel
                      value={
                        authData?.user?.company?.additional_info
                          ?.virtual_interview_link || ''
                      }
                      action={() => setIsVirtualInterviewModal(true)}
                      isEdit
                      textAlign
                    />
                  </Box>

                  {!virtualLocation && (
                    <Typography
                      variant="body2"
                      color="error"
                      fontSize="15px"
                      sx={{ pl: 1, pt: 2 }}
                    >
                      Link is required
                    </Typography>
                  )}
                </>
              )}

              {defaultType === 'hireflix' && (
                <Box
                  sx={{
                    border: '1px solid rgb(233, 213, 255)',
                    px: '10px',
                    py: '10px',
                    borderRadius: '10px',
                    backgroundColor: 'rgb(250, 245, 255)',
                    color: 'rgb(88, 28, 135)',
                    textAlign: 'center',
                  }}
                >
                  <Typography sx={{}}>
                    <strong style={{ color: 'rgb(126, 34, 206)' }}>
                      HireFlix {planStatus}:
                    </strong>{' '}
                    {isTrial ? (
                      trialDaysLeft > 0 ? (
                        <i>
                          Unlimited recorded interviews –{' '}
                          <strong>{trialDaysLeft}</strong> days left on trial
                        </i>
                      ) : (
                        <i>
                          Your HireFlix Trial has expired.{' '}
                          <Button
                            onClick={handleHireflixPlanModal}
                            style={{
                              fontWeight: 'bold',
                              textDecoration: 'underline',
                              color: 'rgb(126, 34, 206)',
                            }}
                          >
                            Manage Credit Plan
                          </Button>
                        </i>
                      )
                    ) : (
                      <i>
                        You have{' '}
                        <strong>{hireflixPlan?.credit_limit ?? 0}</strong>{' '}
                        interview credits remaining
                      </i>
                    )}
                  </Typography>
                </Box>
              )}
            </FormControl>
          )}

        {action?.actionType === 'invite to hireflix interview' && (
          <Box
            sx={{
              border: '1px solid rgb(233, 213, 255)',
              px: '10px',
              py: '10px',
              borderRadius: '10px',
              backgroundColor: 'rgb(250, 245, 255)',
              color: 'rgb(88, 28, 135)',
              textAlign: 'center',
            }}
          >
            <Typography sx={{}}>
              <strong style={{ color: 'rgb(126, 34, 206)' }}>
                HireFlix {planStatus}:
              </strong>{' '}
              {isTrial ? (
                trialDaysLeft > 0 ? (
                  <i>
                    Unlimited recorded interviews –{' '}
                    <strong>{trialDaysLeft}</strong> days left on trial
                  </i>
                ) : (
                  <i>
                    Your HireFlix Trial has expired.{' '}
                    <Button
                      onClick={handleHireflixPlanModal}
                      style={{
                        fontWeight: 'bold',
                        textDecoration: 'underline',
                        color: 'rgb(126, 34, 206)',
                      }}
                    >
                      Manage Credit Plan
                    </Button>
                  </i>
                )
              ) : (
                <i>
                  You have <strong>{hireflixPlan?.credit_limit ?? 0}</strong>{' '}
                  interview credits remaining
                </i>
              )}
            </Typography>
          </Box>
        )}

        {/* invite to interview block */}

        {/* {action?.actionType == 'invite to interview block' &&
          !authData.user?.enterprise && (
            <FormControl sx={{ width: '100%', mt: '10px' }}>
              <RadioGroup
                aria-labelledby="demo-controlled-radio-buttons-group"
                name="controlled-radio-buttons-group"
                value={defaultType} // <-- keep it as "inPerson" | "virtual"
                onChange={(e) =>
                  setDefaultType?.(e.target.value as 'inPerson' | 'virtual')
                }
                sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}
              >
                <FormControlLabel
                  value="inPerson"
                  control={<Radio />}
                  label="In Person"
                  disabled
                />
                <FormControlLabel
                  value="virtual"
                  control={<Radio />}
                  label="Virtual"
                  disabled
                />
              </RadioGroup>

              {physical_address_concate && defaultType === 'inPerson' && (
                <>
                  <Typography variant="body2" sx={{ fontWeight: '600' }}>
                    {authData?.user?.company?.s_name}
                  </Typography>

                  <Typography
                    color="text.primary"
                    fontStyle={physical_address_concate ? 'normal' : 'italic'}
                    whiteSpace="pretty"
                    sx={{ wordBreak: 'break-all', mb: '2px' }}
                  >
                    {physical_address_concate}
                  </Typography>
                </>
              )}

              {authData &&
                authData?.user?.company?.additional_info &&
                authData?.user?.company?.additional_info
                  ?.virtual_interview_link &&
                defaultType === 'virtual' && (
                  <Typography
                    color="text.primary"
                    fontStyle={
                      authData?.user?.company?.additional_info
                        ?.virtual_interview_link
                        ? 'normal'
                        : 'italic'
                    }
                    whiteSpace="pretty"
                    sx={{ wordBreak: 'break-all', mb: '2px' }}
                  >
                    {
                      authData?.user?.company?.additional_info
                        ?.virtual_interview_link
                    }
                  </Typography>
                )}
            </FormControl>
          )} */}

        {/* Invite to Open Interview */}

        {/* {action?.actionType == 'Invite to Open Interview' &&
          !authData.user?.enterprise && (
            <FormControl sx={{ width: '100%', mt: '10px' }}>
              <RadioGroup
                aria-labelledby="demo-controlled-radio-buttons-group"
                name="controlled-radio-buttons-group"
                value={defaultType} // <-- keep it as "inPerson" | "virtual"
                onChange={(e) =>
                  setDefaultType?.(e.target.value as 'inPerson' | 'virtual')
                }
                sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}
              >
                <FormControlLabel
                  value="inPerson"
                  control={<Radio />}
                  label="In Person"
                  disabled
                />
                <FormControlLabel
                  value="virtual"
                  control={<Radio />}
                  label="Virtual"
                  disabled
                />
              </RadioGroup>

              {physical_address_concate && defaultType === 'inPerson' && (
                <>
                  <Typography variant="body2" sx={{ fontWeight: '600' }}>
                    {authData?.user?.company?.s_name}
                  </Typography>

                  <Typography
                    color="text.primary"
                    fontStyle={physical_address_concate ? 'normal' : 'italic'}
                    whiteSpace="pretty"
                    sx={{ wordBreak: 'break-all', mb: '2px' }}
                  >
                    {physical_address_concate}
                  </Typography>
                </>
              )}

              {authData?.user?.company?.additional_info &&
                authData?.user?.company?.additional_info
                  ?.virtual_interview_link &&
                defaultType === 'virtual' && (
                  <Typography
                    color="text.primary"
                    fontStyle={
                      authData?.user?.company?.additional_info
                        ?.virtual_interview_link
                        ? 'normal'
                        : 'italic'
                    }
                    whiteSpace="pretty"
                    sx={{ wordBreak: 'break-all', mb: '2px' }}
                  >
                    {
                      authData?.user?.company?.additional_info
                        ?.virtual_interview_link
                    }
                  </Typography>
                )}
            </FormControl>
          )} */}

        {/* Enterprise level Scheduled Interview' */}
        {action?.actionType === 'Scheduled Interview' &&
          authData.user?.enterprise && (
            <FormControl sx={{ width: '100%', mt: '10px' }}>
              <RadioGroup
                aria-labelledby="demo-controlled-radio-buttons-group"
                name="controlled-radio-buttons-group"
                value={defaultType} // <-- keep it as "inPerson" | "virtual"
                onChange={(e) =>
                  setDefaultType?.(e.target.value as 'inPerson' | 'virtual')
                }
                sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}
              >
                <FormControlLabel
                  value="inPerson"
                  control={<Radio />}
                  label="In Person"
                />
                <FormControlLabel
                  value="virtual"
                  control={<Radio />}
                  label="Virtual"
                />
              </RadioGroup>

              {defaultType === 'inPerson' && (
                <Box
                  sx={{
                    border: '1px solid #e4ecf2',
                    px: '10px',
                    borderRadius: '10px',
                    py: '10px',
                  }}
                >
                  <>
                    <Typography variant="body2" sx={{ fontWeight: '600' }}>
                      {companyData?.object?.s_name}
                    </Typography>

                    <Typography
                      color="text.primary"
                      fontStyle={
                        enterprise_physical_address_concate
                          ? 'normal'
                          : 'italic'
                      }
                      whiteSpace="pretty"
                      sx={{ wordBreak: 'break-all', mb: '2px' }}
                    >
                      {enterprise_physical_address_concate}
                    </Typography>
                  </>
                </Box>
              )}

              {defaultType === 'virtual' && (
                <Box
                  sx={{
                    border: '1px solid #e4ecf2',
                    px: '10px',
                    borderRadius: '10px',
                  }}
                >
                  <Typography
                    color="text.primary"
                    fontStyle={
                      companyData?.object?.additional_info
                        .virtual_interview_link
                        ? 'normal'
                        : 'italic'
                    }
                    whiteSpace="pretty"
                    sx={{ wordBreak: 'break-all', mb: '2px' }}
                  >
                    {
                      companyData?.object?.additional_info
                        .virtual_interview_link
                    }
                  </Typography>

                  {!companyData?.object?.additional_info
                    ?.virtual_interview_link && (
                    <Typography
                      variant="body2"
                      color="red"
                      fontSize="15px"
                      sx={{ pl: 2, py: 2 }}
                    >
                      Please set a virtual interview link at the company level.
                    </Typography>
                  )}
                </Box>
              )}
            </FormControl>
          )}

        {/* Enterprise level Block Interview' */}
        {/* {action?.actionType == 'invite to interview block' &&
          authData.user?.enterprise && (
            <FormControl sx={{ width: '100%', mt: '10px' }}>
              <RadioGroup
                aria-labelledby="demo-controlled-radio-buttons-group"
                name="controlled-radio-buttons-group"
                value={defaultType} // <-- keep it as "inPerson" | "virtual"
                onChange={(e) =>
                  setDefaultType?.(e.target.value as 'inPerson' | 'virtual')
                }
                sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}
              >
                <FormControlLabel
                  value="inPerson"
                  control={<Radio />}
                  label="In Person"
                  disabled
                />
                <FormControlLabel
                  value="virtual"
                  control={<Radio />}
                  label="Virtual"
                  disabled
                />
              </RadioGroup>

              {defaultType === 'inPerson' && (
                <Box
                  sx={{
                    border: '1px solid #e4ecf2',
                    px: '10px',
                    borderRadius: '10px',
                    py: '10px',
                  }}
                >
                  <>
                    <Typography variant="body2" sx={{ fontWeight: '600' }}>
                      {companyData?.object?.s_name}
                    </Typography>

                    <Typography
                      color="text.primary"
                      fontStyle={
                        enterprise_physical_address_concate
                          ? 'normal'
                          : 'italic'
                      }
                      whiteSpace="pretty"
                      sx={{ wordBreak: 'break-all', mb: '2px' }}
                    >
                      {enterprise_physical_address_concate}
                    </Typography>
                  </>
                </Box>
              )}

              {defaultType === 'virtual' && (
                <Box
                  sx={{
                    border: '1px solid #e4ecf2',
                    px: '10px',
                    borderRadius: '10px',
                  }}
                >
                  <Typography
                    color="text.primary"
                    fontStyle={
                      companyData?.object?.additional_info
                        .virtual_interview_link
                        ? 'normal'
                        : 'italic'
                    }
                    whiteSpace="pretty"
                    sx={{ wordBreak: 'break-all', mb: '2px' }}
                  >
                    {
                      companyData?.object?.additional_info
                        .virtual_interview_link
                    }
                  </Typography>

                  {!companyData?.object?.additional_info
                    ?.virtual_interview_link && (
                    <Typography
                      variant="body2"
                      color="red"
                      fontSize="15px"
                      sx={{ pl: 2, py: 2 }}
                    >
                      Please set a virtual interview link at the company level.
                    </Typography>
                  )}
                </Box>
              )}
            </FormControl>
          )} */}

        {/* Enterprise level Block Interview' */}
        {/* {action?.actionType == 'Invite to Open Interview' &&
          authData.user?.enterprise && (
            <FormControl sx={{ width: '100%', mt: '10px' }}>
              <RadioGroup
                aria-labelledby="demo-controlled-radio-buttons-group"
                name="controlled-radio-buttons-group"
                value={defaultType} // <-- keep it as "inPerson" | "virtual"
                onChange={(e) =>
                  setDefaultType?.(e.target.value as 'inPerson' | 'virtual')
                }
                sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}
              >
                <FormControlLabel
                  value="inPerson"
                  control={<Radio />}
                  label="In Person"
                  disabled
                />
                <FormControlLabel
                  value="virtual"
                  control={<Radio />}
                  label="Virtual"
                  disabled
                />
              </RadioGroup>

              {defaultType === 'inPerson' && (
                <Box
                  sx={{
                    border: '1px solid #e4ecf2',
                    px: '10px',
                    borderRadius: '10px',
                    py: '10px',
                  }}
                >
                  <>
                    <Typography variant="body2" sx={{ fontWeight: '600' }}>
                      {companyData?.object?.s_name}
                    </Typography>

                    <Typography
                      color="text.primary"
                      fontStyle={
                        enterprise_physical_address_concate
                          ? 'normal'
                          : 'italic'
                      }
                      whiteSpace="pretty"
                      sx={{ wordBreak: 'break-all', mb: '2px' }}
                    >
                      {enterprise_physical_address_concate}
                    </Typography>
                  </>
                </Box>
              )}

              {defaultType === 'virtual' && (
                <Box
                  sx={{
                    border: '1px solid #e4ecf2',
                    px: '10px',
                    borderRadius: '10px',
                  }}
                >
                  <Typography
                    color="text.primary"
                    fontStyle={
                      companyData?.object?.additional_info
                        .virtual_interview_link
                        ? 'normal'
                        : 'italic'
                    }
                    whiteSpace="pretty"
                    sx={{ wordBreak: 'break-all', mb: '2px' }}
                  >
                    {
                      companyData?.object?.additional_info
                        .virtual_interview_link
                    }
                  </Typography>

                  {!companyData?.object?.additional_info
                    ?.virtual_interview_link && (
                    <Typography
                      variant="body2"
                      color="red"
                      fontSize="15px"
                      sx={{ pl: 2, py: 2 }}
                    >
                      Please set a virtual interview link at the company level.
                    </Typography>
                  )}
                </Box>
              )}
            </FormControl>
          )} */}

        <Stack spacing={3} sx={{ marginBottom: 4, mt: 2 }}>
          {errorText && <Alert severity="error">{errorText}</Alert>}
          {showProgress && (
            <LinearProgress
              color="success"
              variant="determinate"
              value={progress}
            />
          )}

          {isSuccessMessageShown ? (
            <Alert severity="success">{successMessage}</Alert>
          ) : (
            <>
              {isDateRangePickerShown && defaultType !== 'hireflix' && (
                <SelectDateAndTimeForm
                  setStartTimestamp={setStartTimestamp!}
                  setEndTimestamp={setEndTimestamp!}
                  setFormattedDate={setFormattedDate!}
                />
              )}
              {action?.actionType !== 'assign Tags' ? (
                <>
                  <BundledEditor
                    onInit={(evt: any, editor: TinyMCEEditor | null) =>
                      (editorRef.current = editor)
                    }
                    value={message}
                    onEditorChange={(newValue: string) => setMessage(newValue)}
                    init={{
                      height: 200,
                      menubar: false,
                      plugins: ['lists', 'autolink', 'link', 'pagebreak'],
                      toolbar:
                        'spellchecker language spellcheckdialog | styles | bold italic | bullist numlist | link',
                      content_style: 'textarea  { border-radius: 8px; }',
                      spellchecker_dialog: true,
                      spellchecker_rpc_url: 'localhost/ephox-spelling',
                      spellchecker_language: 'en_US',
                      spellchecker_active: true,
                      browser_spellcheck: true,
                      contextmenu: false,
                    }}
                  />

                  {action?.title !== 'Message' &&
                    message !== initialMessage && (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={shouldSaveNewDefaultMessage}
                            onChange={(e) =>
                              setShouldSaveNewDefaultMessage(e.target.checked)
                            }
                          />
                        }
                        label="Save as a default message"
                      />
                    )}
                  <Stack
                    spacing={2}
                    width="100%"
                    direction={{ xs: 'column', sm: 'row' }}
                  >
                    {defaultType === 'hireflix' ||
                    action.actionType === 'invite to hireflix interview' ? (
                      <LoadingButton
                        onClick={() => {
                          handleSubmit(true, message);
                          handleSaveDefaultMessage();
                        }}
                        sx={{
                          width: '100%',
                          backgroundColor: hireflixCta,
                          '&:hover': {
                            backgroundColor: hireflixCtaHover,
                          },
                        }}
                        loading={showProgress}
                        variant="contained"
                        color={buttonColor}
                        disabled={
                          hireflixInviteLoading ||
                          (isTrial && trialDaysLeft === 0)
                        }
                        startIcon={<DoneIcon />}
                      >
                        Send Hireflix Interview Invite
                      </LoadingButton>
                    ) : (
                      <LoadingButton
                        onClick={() => {
                          handleSubmit(true, message);
                          handleSaveDefaultMessage();
                        }}
                        sx={{ width: '100%' }}
                        loading={showProgress}
                        variant="contained"
                        color={buttonColor}
                        disabled={
                          // enterprise_virtual_Link ||
                          // isLink ||
                          !message.length ||
                          getStrippedText(message).length === 0 ||
                          disabled ||
                          (defaultType === 'virtual' && !virtualLocation)
                        }
                        startIcon={<DoneIcon />}
                      >
                        {submitWithMessageText}
                      </LoadingButton>
                    )}
                    {showSendWithoutMessage && (
                      <LoadingButton
                        onClick={() => {
                          handleSubmit(false, message);
                          handleSaveDefaultMessage();
                        }}
                        sx={{ width: '100%' }}
                        loading={showProgress}
                        variant="outlined"
                        color={
                          action.actionType === 'hire' ? 'purple' : buttonColor
                        }
                        startIcon={<DoneIcon />}
                        disabled={disabled}
                      >
                        {submitWithoutMessageText}
                      </LoadingButton>
                    )}
                  </Stack>
                </>
              ) : (
                <>
                  {authData.user?.enterprise ? (
                    <AssignTagsModal
                      selectedCompanyId={selectedCompanyId}
                      handleClose={handleClose}
                      selectedApplicationsIds={selectedApplicationsIds}
                      handlePageNo={handlePageNo}
                    />
                  ) : (
                    <EmployerAssignTagsModal
                      handleClose={handleClose}
                      selectedApplicationsIds={selectedApplicationsIds}
                      handlePageNo={handlePageNo}
                    />
                  )}
                </>
              )}
            </>
          )}
        </Stack>
      </DialogContent>

      {/* .....Virtual link Modal... */}
      <DynamicModal
        open={isVirtualInterviewModal}
        onClose={() => {
          setIsVirtualInterviewModal(false);
          setVirtualLocation(virtualLocation);
        }}
        title="Enter Your Link"
        onSave={handleVirtualLinkSave}
        icon={<EditIcon />}
        loading={isUpdatingProfile}
      >
        <TextField
          placeholder="Enter meeting link"
          value={virtualLocation}
          onChange={(e: any) => setVirtualLocation(e.target.value)}
          fullWidth
          error={
            (virtualLocation !== '' && !isValidHttpUrl(virtualLocation)) ||
            virtualLocation === ''
          }
          helperText={
            virtualLocation === ''
              ? 'Meeting link is required'
              : !isValidHttpUrl(virtualLocation)
                ? 'Invalid URL'
                : ''
          }
        />
      </DynamicModal>

      {/* .....Address Modal... */}
      <DynamicModal
        open={isUpdateAddressOpen}
        onClose={() => {
          setIsUpdateAddressOpen(false);
          setPhysicalLocation(physical_address_concate);
        }}
        title="Enter Your Address"
        onSave={handleSave}
        loading={isUpdatingProfile}
        icon={
          <HomeIcon
            sx={{
              fontSize: 60,
              color: 'primary.main',
              margin: 'auto !important',
            }}
          />
        }
      >
        <TextField
          placeholder="Enter company address..."
          value={physicalLocation}
          onChange={(e: any) => setPhysicalLocation(e.target.value)}
          fullWidth
          multiline
          rows={3}
        />
      </DynamicModal>

      {/* Hireflix Plan Purchase Modal */}
      {hireflixPlanModal && (
        <HireflixPlanPurchaseModal
          isOpen={hireflixPlanModal}
          handleClose={handleHireflixPlanModal}
        />
      )}
    </Dialog>
  );
};

export default SingleMessageModal;
